/**
 * @fileoverview DoorRow: a worded row on a card that opens something (B158). A 44px button with a leading muted icon, the words
 * and a chevron at the right, inset 16px like the card's other rows. The default is the ordinary ink; `tone="destructive"` is
 * offered for a caller that needs the red, though the bed card's Clear row does not use it.
 *
 * It draws the words it is given and decides nothing.
 *
 * Where: no caller yet; the bed card's "A note for every plant..." and "Clear the bed for the season..." rows adopt it in B158
 * jobs 4 and 7.
 * API: none.
 * Words: none; the caller gives them.
 *
 * @module DoorRow
 */

import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/** What the row is given, and any button attribute. */
export interface DoorRowProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The leading icon, as an element (sized by the row). */
  icon: React.ReactNode;
  /** The ink: the ordinary one, or the destructive red. */
  tone?: 'default' | 'destructive';
  /** The row's words. */
  children?: React.ReactNode;
}

/**
 * The door row: icon, words, chevron.
 *
 * @param props - DoorRowProps.
 */
export const DoorRow: React.FC<DoorRowProps> = ({ icon, tone = 'default', className, children, ...rest }) => (
  <button
    type="button"
    className={cn(
      'flex min-h-11 w-full min-w-0 items-center gap-2 rounded-md px-4 py-2 text-left text-sm font-semibold transition-colors hover:bg-muted/50 active:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      tone === 'destructive' ? 'text-destructive' : 'text-foreground',
      className,
    )}
    {...rest}
  >
    <span className="inline-flex shrink-0 items-center text-muted-foreground [&_svg]:h-4 [&_svg]:w-4" aria-hidden="true">{icon}</span>
    <span className="min-w-0 flex-1">{children}</span>
    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
  </button>
);
